import { useRef, useState } from "preact/hooks";
import { importIfcFile, type ImportedIfcModel } from "../domain/ifcModelImport";

type SampleFixture = { file: string; label: string };

// Bundled under public/ifc-samples/ (static passthrough, not a JS-bundled asset — see
// public/ifc-samples/NOTICE.md for exact provenance/license). Byte-identical copies of the same
// buildingSMART sample files aec-project-controls already vetted and shipped.
const SAMPLE_FIXTURES: readonly SampleFixture[] = [
  { file: "minimal_wall_u1_2026-07.ifc", label: "Minimal wall" },
  { file: "bsi-wall-with-opening-and-window.ifc", label: "Wall + window (bSI)" },
  { file: "ifc43-bsi-pcert-building-architecture.ifc", label: "Building (bSI, real storeys)" },
];

type Props = {
  onImported: (model: ImportedIfcModel) => void;
};

export function IfcImportControls({ onImported }: Props) {
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const runImport = async (source: File | { sampleFile: string }) => {
    setIsBusy(true);
    setError(null);
    setStatus("Opening engine…");
    try {
      const file =
        "sampleFile" in source
          ? await (async () => {
              const response = await fetch(`/ifc-samples/${source.sampleFile}`);
              if (!response.ok) throw new Error(`Could not load sample fixture: ${source.sampleFile}`);
              const bytes = await response.arrayBuffer();
              return new File([bytes], source.sampleFile, { type: "application/octet-stream" });
            })()
          : source;
      const model = await importIfcFile(file, (message) => setStatus(message));
      setStatus(`${model.elements.length} elements · ${model.storeys.length} storeys · ${model.triangleCount.toLocaleString("en-GB")} triangles`);
      onImported(model);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      setStatus(null);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="ifc-import">
      <div className="ifc-import-row">
        <button type="button" className="primary-button" disabled={isBusy} onClick={() => fileInputRef.current?.click()}>
          {isBusy ? "Importing…" : "Upload IFC file"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".ifc"
          className="visually-hidden"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file) void runImport(file);
            event.currentTarget.value = "";
          }}
        />
      </div>
      <div className="ifc-import-samples">
        <span>Or try a sample</span>
        {SAMPLE_FIXTURES.map((sample) => (
          <button type="button" key={sample.file} className="secondary-button" disabled={isBusy} onClick={() => void runImport({ sampleFile: sample.file })}>
            {sample.label}
          </button>
        ))}
      </div>
      {status && <p className="ifc-import-status" role="status">{status}</p>}
      {error && <p className="draft-error" role="alert">{error}</p>}
      <p className="ifc-import-note">Parsing and rendering happen entirely in this browser. The file is never uploaded anywhere.</p>
    </div>
  );
}
